import React, { MutableRefObject, useState } from 'react';
import {
  SecondaryButton,
  TertiaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import { withLocalePrefix } from 'shared/utils/translation';
import AiMagicIcon from '@grnhse/seedling/lib/birch/icons/ai_magic';
import Popper from '@mui/material/Popper';
import Box from '@mui/material/Box';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import Api from '../lib/api';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import TextInput from '@grnhse/seedling/lib/birch/components/forms/text_input';
import SkeletonLoader from '@grnhse/seedling/lib/birch/components/skeleton_loader';
import { Chip } from '@grnhse/seedling/lib/birch/components/chips';
import { WarningCallOut } from '@grnhse/seedling/lib/birch/components/call_out';
import { Tooltip } from '@grnhse/seedling/lib/birch/components/tooltips';

const t = withLocalePrefix('talent_rediscovery.form.popper');

type Props = {
  hasAiKeywords: boolean;
  anchorEl: MutableRefObject<HTMLElement | null>;
  apiClient: Api;
  setGeneratedKeywords: (keywords: string[]) => void;
};

const boxStyle = (parentWidth: number) => {
  return {
    border: `1px solid ${FullPalette.GREY_600}`,
    borderRadius: '4px',
    boxShadow: `0px 3px 11px 0px ${FullPalette.EVERGREEN_E200}`,
    bgcolor: 'background.paper',
    marginTop: '8px',
    marginRight: '-8px',
    width: `${parentWidth * 0.98}px`,
  };
};

const GenerateKeywordsPopper = ({
  hasAiKeywords,
  anchorEl,
  apiClient,
  setGeneratedKeywords,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [userPrompt, setUserPrompt] = useState('');
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const parentWidth = anchorEl?.current?.parentElement?.offsetWidth || 0;

  const generateSuggestions = async () => {
    setError('');
    setSuggestions([]);
    setLoading(true);

    try {
      const { terms, error_message } = await apiClient.fetchRediscoveryKeywords(
        userPrompt
      );

      if (error_message) {
        setError(error_message);
        return;
      }

      setSuggestions(terms);
    } catch (e) {
      setError(t('generic_error'));
    } finally {
      setLoading(false);
    }
  };

  const closePopper = () => {
    setSuggestions([]);
    setUserPrompt('');
    setError('');
    setOpen(false);
  };

  const acceptSuggestions = () => {
    setGeneratedKeywords(suggestions);
    closePopper();
  };

  return (
    <>
      <Tooltip
        label={t('generate_button_tooltip')}
        ariaLabel={t('generate_button_tooltip')}
      >
        <SecondaryButton
          data-provides={'rediscovery-keywords-button'}
          onClick={() => setOpen(true)}
          icon={AiMagicIcon}
          size={'sm'}
        >
          {hasAiKeywords ? t('regenerate_button') : t('generate_button')}
        </SecondaryButton>
      </Tooltip>
      {anchorEl?.current && (
        <Popper
          open={open}
          anchorEl={anchorEl.current}
          placement={'bottom-end'}
        >
          <ClickAwayListener onClickAway={closePopper}>
            <Box sx={boxStyle(parentWidth)}>
              <Stack sx={{ margin: 2.5 }} data-provides={'popper-form'}>
                {loading && (
                  <SkeletonLoader
                    columnsPerRow={1}
                    rows={3}
                    cellHeight={'16px'}
                    spaceBetweenRows={'4px'}
                  />
                )}

                {error && <WarningCallOut bodyText={error} />}

                {suggestions.length > 0 && (
                  <Stack>
                    <Grid
                      sx={{ display: 'flex', flexWrap: 'wrap' }}
                      data-provides={'suggestions-list'}
                    >
                      {suggestions.map((suggestion) => (
                        <Grid
                          sx={{ marginRight: 1, marginTop: 1 }}
                          key={suggestion}
                        >
                          <Chip text={suggestion} truncated={false} />
                        </Grid>
                      ))}
                    </Grid>
                    <Grid
                      sx={{ display: 'flex', gap: '16px' }}
                      direction="row"
                      alignItems="center"
                    >
                      <SecondaryButton onClick={acceptSuggestions} size={'md'}>
                        {t('accept_suggestions')}
                      </SecondaryButton>
                      <TertiaryButton onClick={closePopper}>
                        {t('cancel')}
                      </TertiaryButton>
                    </Grid>
                  </Stack>
                )}

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Grid sx={{ flexGrow: 1 }}>
                    <TextInput
                      ariaLabel={t('placeholder')}
                      placeholder={t('placeholder')}
                      onChange={(e: React.SyntheticEvent<HTMLInputElement>) =>
                        setUserPrompt(e.currentTarget.value)
                      }
                      value={userPrompt}
                      maxLength={200}
                    />
                  </Grid>

                  <Grid>
                    <SecondaryButton
                      data-provides={'generate-keywords-button'}
                      onClick={generateSuggestions}
                      disabled={loading || !userPrompt}
                      size={'md'}
                    >
                      {t('generate')}
                    </SecondaryButton>
                  </Grid>
                </Stack>
              </Stack>
            </Box>
          </ClickAwayListener>
        </Popper>
      )}
    </>
  );
};

export default GenerateKeywordsPopper;
